import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Banner } from "../components/Banner";
import { Pill } from "../components/Pill";
import { DataTable, type Column } from "../components/DataTable";
import { api } from "../lib/api";
import "./AdminPage.css";

interface ClientRow {
  client_id: string;
  owner_email: string;
  display_name_real: string | null;
  signup_at: string;
  status: string;
  password_set_at: string | null;
}

interface ListResponse {
  clients: ClientRow[];
}

interface CreateResponse {
  ok: boolean;
  clientId: string;
  emailSent: boolean;
  emailError?: string;
  setupUrl: string;
}

type Toast = { tone: "ok" | "err"; text: string; details?: string } | null;

export function AdminPage() {
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  async function refresh() {
    try {
      const r = await api<ListResponse>("/api/admin/clients");
      setClients(r.clients);
    } catch (e) {
      setErr((e as Error).message ?? "Failed to load");
    }
  }

  useEffect(() => { refresh(); }, []);

  async function createClient(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setToast(null);
    try {
      const r = await api<CreateResponse>("/api/admin/clients", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: newEmail, displayName: newName || undefined }),
      });
      setNewEmail("");
      setNewName("");
      await refresh();
      if (r.emailSent) {
        setToast({ tone: "ok", text: `Account created. Setup email sent to ${newEmail}.` });
      } else {
        setToast({
          tone: "err",
          text: "Account created but email failed.",
          details: `Send this URL manually:\n${r.setupUrl}`,
        });
      }
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message ?? "Failed to create" });
    } finally {
      setBusy(false);
    }
  }

  async function resendSetup(clientId: string) {
    setBusy(true);
    setToast(null);
    try {
      const r = await api<{ ok: boolean; emailSent: boolean; setupUrl: string; emailError?: string }>(
        `/api/admin/clients/${encodeURIComponent(clientId)}/setup-link`,
        { method: "POST" },
      );
      if (r.emailSent) setToast({ tone: "ok", text: "Setup link resent." });
      else setToast({ tone: "err", text: "Email failed.", details: `Send this URL manually:\n${r.setupUrl}` });
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message ?? "Failed" });
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<ClientRow>[] = [
    { key: "email", label: "Email", align: "left", render: (c) => c.owner_email },
    {
      key: "name",
      label: "Name",
      align: "left",
      render: (c) => c.display_name_real ?? <span style={{ color: "var(--color-bone-dim)" }}>—</span>,
    },
    {
      key: "status",
      label: "Status",
      align: "left",
      render: (c) =>
        c.password_set_at ? <Pill tone="ok">Active</Pill> : <Pill tone="warn">Awaiting setup</Pill>,
    },
    {
      key: "signup",
      label: "Created",
      align: "left",
      render: (c) => new Date(c.signup_at).toLocaleDateString(),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (c) => (
        <Button size="sm" variant="ghost" onClick={() => resendSetup(c.client_id)} disabled={busy}>
          {c.password_set_at ? "Send reset link" : "Resend setup"}
        </Button>
      ),
    },
  ];

  return (
    <Layout clientId={undefined}>
      <div style={{ marginBottom: "var(--space-5)" }}>
        <span className="label">Operator</span>
        <h1 style={{ marginTop: "6px" }}>Admin</h1>
        <p style={{ color: "var(--color-bone-dim)", margin: "8px 0 0" }}>
          Create accounts and resend setup links. Visible only to <code>OPERATOR_EMAIL</code>.
        </p>
      </div>

      {toast && (
        <Banner tone={toast.tone}>
          {toast.text}
          {toast.details && (
            <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0", fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {toast.details}
            </pre>
          )}
        </Banner>
      )}
      {err && <Banner tone="err">{err}</Banner>}

      <Card title="Create new client">
        <form className="admin-form" onSubmit={createClient}>
          <label>
            <span className="auth-form__label">Email</span>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            <span className="auth-form__label">Display name (optional)</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={busy}
            />
          </label>
          <Button type="submit" disabled={busy || !newEmail.includes("@")}>
            Create + send setup email
          </Button>
        </form>
      </Card>

      {clients === null && !err && <Banner tone="info">Loading…</Banner>}
      {clients && (
        <Card padding="none" title={`Clients (${clients.length})`}>
          <DataTable
            columns={columns}
            rows={clients}
            rowKey={(c) => c.client_id}
            emptyMessage="No clients yet."
          />
        </Card>
      )}
    </Layout>
  );
}
